import { useRef, useEffect, FC, useState, useCallback } from 'react';
import { TempusDominus, Namespace } from '@eonasdan/tempus-dominus';
import { format, isEqual } from 'date-fns';

import { KTSVG } from '../../../_metronic/helpers';
import clsx from 'clsx';

const DATE_FORMATS = {
  LTS: 'h:mm:ss T',
  LT: 'h:mm T',
  L: 'MM/dd/yyyy',
  LL: 'MMMM d yyyy',
  LLL: 'MMMM d yyyy h:mm aa',
  LLLL: 'dddd, MMMM d, yyyy h:mm aa'
};


type Props = {
  className: string
  selected: Date | null
  onChange: (dt: Date | null) => void
  dateFormat? : string
  showClock? : boolean
  placeholderText? : string
  showShiftSelector? : boolean
  asRow?: boolean
}

// https://18.react.dev/reference/react-dom/components/common#ref-callback
export const DateSelector: FC<Props>  = ({className, selected, onChange, dateFormat = 'LL', showClock = false, placeholderText = '', showShiftSelector = false, asRow = false}) => {

  const [ selected_shift, setShift ] = useState('');

  const tempusRef = useRef<TempusDominus | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const subsRef = useRef<any>(null);

  const setRef = useCallback((node: HTMLInputElement | null) => {
    // cleanup
    if (inputRef.current) {
      if (tempusRef.current !== null) {    
        if (subsRef.current !== null) {          
          subsRef.current.unsubscribe();        
          subsRef.current = null;
        }
        tempusRef.current.dispose();
        tempusRef.current = null;                
      }            
    }
    
    // assign node
    inputRef.current = node;
    // setup tempusDominus
    if (node !== null) {
      if (node.dataset?.initdate) {
        node.value = node.dataset.initdate;
      }
    
      tempusRef.current = new TempusDominus(
        node,
        {
          display: {
            viewMode: 'calendar',
            theme: 'light',
            components : {
              clock: showClock,
              hours: true,
              minutes: true,
              seconds: false,
            }
          },
          localization: {
            dayViewHeaderFormat: { month: 'long', year: 'numeric' },
            format: dateFormat,
            dateFormats: DATE_FORMATS,
          },                
        }
      );     
      if (onChange) {
        subsRef.current = tempusRef.current.subscribe(Namespace.events.change, (e: any) => {                
          onChange(e.isValid ? e.date : null);                
        });                    
      } 
    }        
  }, [showClock, dateFormat, onChange]);

  const doClearDate = useCallback(() => {
    if (tempusRef.current) tempusRef.current.clear();    
    onChange(null);    
  }, [onChange]);


  // update input value
  useEffect(() => {        
    
    if (! selected) {
      if (inputRef.current) inputRef.current.value = '';
      doClearDate();
      return;
    }    
    
    let adjusted = new Date(selected);    
    let dt_format =  DATE_FORMATS[dateFormat as keyof typeof DATE_FORMATS];

    if (showShiftSelector) {
      dt_format = DATE_FORMATS.LL;
      switch(selected_shift) {
        case 'half-am': 
          adjusted.setHours(9,0,0);
          break;
        case 'half-pm': 
          adjusted.setHours(15,0,0);
          break;
        case 'full-day': 
          adjusted.setHours(12,0,0);
          break;
        default:        
          let nowDate = new Date();
          adjusted.setHours(nowDate.getHours(), nowDate.getMinutes(), 0);        
          dt_format = DATE_FORMATS.LLL;
          break;
      }
    }

    if (inputRef.current) inputRef.current.value = format(adjusted,dt_format);  
    if (! isEqual(selected, adjusted)) {                        
      onChange(adjusted);        
    }

    // detach reference to inputCurrent
    // eslint-disable-next-line
  }, [selected_shift, selected, dateFormat, doClearDate, showShiftSelector, onChange]);

  return (
  <div className="row fv-row" >
    <div className={clsx('position-relative min-w-125px', {'col-12' : (!(asRow && showShiftSelector))}, {'col-6' : (asRow && showShiftSelector)})} >
      <input 
        ref={setRef}
        className={className + " pe-8"}
        type="text"            
        readOnly={true}      
        placeholder={placeholderText}      
        data-initdate={selected && dateFormat ? format(selected, DATE_FORMATS[dateFormat as keyof typeof DATE_FORMATS]): undefined}
      />
      <div 
        className="position-absolute btn-active-warning btn btn-outline p-0 fs-10" 
        style={{top:'0.25rem', right: '1rem'}} 
        onClick={doClearDate} 
        title="Clear date"
      >
        <KTSVG path='/media/icons/duotune/arrows/arr097.svg' className='svg-icon-6 m-0 lh-0' />  
      </div>
    </div>
    { showShiftSelector && 
    <div className={clsx({'col-6 ms-n2' : asRow}, {'col-12 mt-1' : (! asRow)})}>
      <select 
        name="selected_shift"
        value={selected_shift}      
        className='form-select form-select-sm form-select-solid fs-7 fw-bold mb-2 min-w-125px'
        onChange={(e:any) => setShift(e.target.value)}
      >
        <option></option>
        <option value="half-am">Half Day (AM)</option>
        <option value="half-pm">Half Day (PM)</option>
        <option value="full-day">Full Day</option>
      </select>
    </div>
    }
  </div>
  )
}